import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { AppControlApis, DataFlowApis } from "@core/ability";
import useServiceConfigStore, {
  ServiceType,
} from "@/store/serviceConfig.store";
import IC_BACK from "@/assets/login/ic_back.svg";
import { message } from 'antd';

// 服务类型选项配置
const serviceTypeOptions = [
  {
    type: ServiceType.PUBLIC,
    label: "公有云服务",
  },
  {
    type: ServiceType.PRIVATE,
    label: "私有云服务",
  },
];

interface ServiceConfigProps {
  onConfirm?: () => void;
  onCancel: () => void;
}

// 服务配置页面组件
const ServiceConfig: React.FC<ServiceConfigProps> = ({
  onConfirm,
  onCancel,
}) => {
  // 使用服务配置 store
  const {
    config: serviceConfig,
    saveConfig: saveConfigToServer
  } = useServiceConfigStore();

  // 组件内部状态
  const [serviceType, setServiceType] = useState<ServiceType>(
    serviceConfig.serviceType || ServiceType.PUBLIC
  );
  const [serviceAddress, setServiceAddress] = useState(
    serviceConfig.serviceAddress || ""
  );
  const [addressValid, setAddressValid] = useState(
    !serviceConfig.serviceAddress ||
    serviceConfig.serviceAddress.trim().length > 0
  );

  // 重启确认弹窗状态
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  // 检测配置是否发生变化
  const hasConfigChanged = useMemo(() => {
    // 服务类型变化：公切私、私切公
    if (serviceType !== serviceConfig.serviceType) {
      return true;
    }

    // 私有云服务的地址变化
    if (serviceType === ServiceType.PRIVATE) {
      return serviceAddress !== serviceConfig.serviceAddress;
    }

    return false;
  }, [serviceType, serviceAddress, serviceConfig]);

  // 地址输入处理
  const onServerAddressChange = (value: string) => {
    setServiceAddress(value);
    // 简单的地址验证
    const isValid = value.trim().length > 0;
    setAddressValid(isValid);
  };

  // 确认按钮禁用状态
  const isServiceConfirmDisabled = useMemo(() => {
    if (serviceType === ServiceType.PRIVATE) {
      return !serviceAddress.trim();
    }
    return false;
  }, [serviceType, serviceAddress]);

  // 确认配置处理
  const handleConfirmServiceConfig = async () => {
    if (configLoading) return;
    let tempAddress = serviceAddress;

    if (serviceType === ServiceType.PRIVATE) {
      if (!tempAddress.trim()) {
        message.warning("服务地址不能为空");
        return;
      }
      //服务地址不能以/结尾--多了/会导致健康检测能过，但是页面访问不了
      if (tempAddress.endsWith('/')) {
        message.warning("服务地址错误");
        return;
      }

      //没有http://，手动加一个，私有化配置只需要用户输IP就行
      if (!tempAddress.startsWith('http://') && !tempAddress.startsWith('https://')) {
        tempAddress = 'http://' + tempAddress;
      }

      try {
        const res = await DataFlowApis.checkHealth2(tempAddress);
        if (!res) {
          message.warning("服务地址错误");
          return;
        }
      } catch (e) {
        message.warning("服务地址错误");
        return;
      }
    }

    if (!hasConfigChanged) {
      onCancel();
      return;
    }

    setShowRestartConfirm(true);
  };

  // 保存配置
  const saveConfig = async () => {
    setConfigLoading(true);

    try {
      const newConfig = {
        serviceType,
        serviceAddress: serviceAddress,
      };

      // 使用服务器API保存配置
      const success = await saveConfigToServer(newConfig);

      if (success) {
        onConfirm?.();
      } else {
        console.error("保存服务配置失败");
      }
    } finally {
      setConfigLoading(false);
    }
  };

  // 确认重启
  const handleConfirmRestart = () => {
    if (configLoading) return;

    setShowRestartConfirm(false);
    saveConfig();
    console.log("应用将重启以应用新配置");
    AppControlApis.restartApp();
  };

  // 取消重启
  const handleCancelRestart = () => {
    setShowRestartConfirm(false);
  };

  // 重启确认弹窗组件
  const RestartConfirmModal = () => {
    if (!showRestartConfirm) return null;

    return (
      <div
        className="fixed bg-#1F2533B2 flex items-center justify-center"
        style={{
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 10000,
          margin: 0,
          padding: 0,
        }}
      >
        <div className="bg-white rd-2.5 p-6 w-80 mx-4">
          <div className="text-size-3.5 font-500 h-13 lh-5.5 text-#1F2533 mb-4">
            重启应用后，服务配置方可生效
          </div>

          <div className="flex space-x-4 justify-end">
            <button
              className="w-20 h-8 lh-5.5 border-1 border-solid border-#ECEEF3 bg-#ffffff rd-1.5 flex flex-items-center flex-justify-center text-size-3.5 text-#30343C cursor-pointer"
              onClick={handleCancelRestart}
            >
              取消配置
            </button>

            <button
              className="w-20 h-8 lh-5.5 border-1 border-solid border-#ECEEF3 bg-#FF6B47 rd-1.5 flex flex-items-center flex-justify-center text-size-3.5 text-#FFFFFF cursor-pointer"
              onClick={handleConfirmRestart}
            >
              立即重启
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white flex flex-col flex-items-center px-9 relative left-1/2 top-1/2 -translate-1/2">
      <div className="w-90 mt-6 mb-6">
        <button
          className="flex flex-items-center text-#191D26 text-size-4 cursor-pointer border-0 bg-transparent"
          onClick={onCancel}
        >
          <img src={IC_BACK} alt="back" />
          <span className="ml-1">返回</span>
        </button>
      </div>

      <div className="w-80 mb-8">
        <h1 className="text-#1F2533 text-size-6 select-none">服务配置</h1>
      </div>

      <div className="w-80 flex flex-col mx-auto">
        {/* 服务类型选择 */}
        <div className="h-55">
          <div className="mb-6">
            <div className="flex flex-col space-y-8">
              {serviceTypeOptions.map((option) => (
                <div
                  key={option.type}
                  className="flex flex-items-center cursor-pointer"
                  onClick={() => setServiceType(option.type)}
                >
                  <div
                    className={`w-4 h-4 rd-50% border-solid border-1 mr-3 ml-1 flex flex-items-center flex-justify-center ${serviceType === option.type
                        ? "border-#4981F2"
                        : "border-#1A1D2633"
                      }`}
                  >
                    {serviceType === option.type && (
                      <div className="w-2.5 h-2.5 rd-50% bg-#4981F2"></div>
                    )}
                  </div>
                  <span
                    className={`text-size-4 ${serviceType === option.type
                        ? "text-#4981F2"
                        : "text-#30343C"
                      }`}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {serviceType === ServiceType.PRIVATE && (
            <div>
              <div className="mb-16">
                <div
                  className={`h-12 bg-#F4F6F9 rd-2.5 border-1 border-solid py-1 px-3 transition-colors ${!addressValid && serviceAddress
                      ? "border-#FF584C"
                      : "border-transparent focus-within:border-#4981F2"
                    }`}
                >
                  <input
                    className="w-full h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-3.5 placeholder:font-400"
                    placeholder="请输入服务器地址（必填）"
                    value={serviceAddress}
                    onChange={(e) => onServerAddressChange(e.target.value)}
                  />
                </div>
                {!addressValid && serviceAddress && (
                  <div className="h-5 text-size-3 text-#FF584C ml-4 mt-1">
                    服务器地址不能为空
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 按钮组 */}
        <div className="flex flex-justify-center mt-33">
          <button
            disabled={isServiceConfirmDisabled}
            className={`flex-1 h-12 bg-#293143 rd-2.5 flex flex-items-center flex-justify-center text-size-3.5 border-none ${isServiceConfirmDisabled
                ? "text-#6B7280 cursor-not-allowed"
                : "text-#FFFFFF cursor-pointer"
              }`}
            onClick={handleConfirmServiceConfig}
          >
            确定
          </button>
        </div>
      </div>
      {createPortal(<RestartConfirmModal />, document.body)}
    </div>
  );
};

export default ServiceConfig;
